import type { CallHistoryMethodAction } from 'connected-react-router';
import type { Action, Reducer } from 'redux';

import IBooking, { IBookingAddOn, IBookingDigitalAddOn } from '../interfaces/IBooking';
import { Routes, navigateTo } from '../helpers/routes';

import { ICharge } from '../interfaces/ICharge';
import { IDiscountCode } from '../interfaces/IDiscountCode';
import Session from '../interfaces/ISession';
import BookingService from '../service/bookings';
import Logging from '../service/logging';
import { IApplicationState, IAnalyticsAction, IAppThunkAction } from './';
import IBookingDetails from '../interfaces/IBookingDetails';
import { GoogleTagManagerEvents } from '../helpers/googleTagManager';
import _ from 'lodash';
import { PurchaseTypes } from './PurchaseTypes';
import { IPriceSet } from '../interfaces/IPriceSet';
import BrazeEvents from '../helpers/braze';
import { appInsights } from '../Logging/appInsights';
import { getEffectiveCharge } from '../helpers/effectiveCharge';
import { IAvailableAddOn } from '../interfaces/IAvailableAddOn';
import { IAvailableDigitalAddOn } from '../interfaces/IAvailableDigitalAddOn';
import { IUpdateBookingAddOn, IUpdateBookingDigitalAddOn } from '../interfaces/IUpdateBookingRequest';
import { IRewardFlowRequestData } from '../interfaces/IRewardFlow';
import { PlayerCountUpsellKeys, PlayerCountUpsellFlow } from '../helpers/PlayerCountUpsellFlow';
import IPaymentRequest from '../interfaces/IPaymentRequest';
// Customer contact data (email, name, phone) lives in the Customer store, not here.
// Booking thunks read from appState.customer when making API calls. This is temporary
// coupling — when a dedicated customer sync API is added, these reads should be removed
// and customer syncing should happen independently. Do not add customer fields to IBookingState.
import { actionCreators as CustomerActions, formatPhoneNumber } from './Customer';
import { PaymentOptionType } from './PaymentOption';
import { actionCreators as UIActions } from './UI';
import PlayersService from '../service/players';
import { PLAYERS_SELECTED } from './Players';

export enum BookingStatus {
    Unknown = 0,
    Pending = 1,
    Active = 2,
    Cancelled = 3,
    Refunded = 4,
    Expired = 5,
    Failed = 6,
    //Completed = 7,
    Reserved = 8,
}

export const paymentOptionTypeIdMap: Record<PaymentOptionType, number> = {
    full: 0,
    deposit: 1,
    split: 2,
};

export enum DiscountCodeResponseType {
    NotFound,
    Inactive,
    Used,
    Available,
    FailedSlotRequirement,
    Expired,
    InvalidClient,
    InvalidSite,
    NotOpenYet,
    InvalidSchedule,
    IncorrectPlayerAccount,
    IncorrectPackageType,
    RedeemLimitExceeded,
    IncorrectAttractionType,
    NoPromoWithGiftVoucher,
    NoPromoWithPriceOverride,
    InvalidCurrency
}

export type BookingFlowKey =
    | 'discountCode'       // apply / remove discount
    | 'paymentOption'      // deposit / split toggle
    | 'addons'             // applyAddOns
    | 'reserve'            // reserveBooking
    | 'confirm'            // final pay
    | 'expire'             // expireBooking
    | 'detailsLoad'        // booking details / check booking fetches
    | 'bookingSlots'       // updateBookingSlots (player count change)
    | 'bookingCompoundUpdate'; // updateBooking / changeUser (customer sync + promo code application)

export type BookingInFlight = Partial<Record<BookingFlowKey, true>>;

export interface IBookingState {
    loading: boolean;
    inFlight: BookingInFlight;
    failed: boolean;
    failedMessageKey?: string;
    booking?: IBooking;
    charge?: ICharge;
    status: BookingStatus;
    preSelectedCode?: string;
    appliedCodes: Array<IDiscountCode>;
    codeApplied?: boolean;
    details?: IBookingDetails;
    priceSets?: Array<IPriceSet>;
    readyForPayment: boolean;
    codeResponse?: DiscountCodeResponseType;
    availableDigitalAddOns?: Array<IAvailableDigitalAddOn>;
    availableAddOns?: Array<IAvailableAddOn>;
    selectedAddOns?: Array<IUpdateBookingAddOn>;
    selectedDigitalAddOns?: Array<IUpdateBookingDigitalAddOn>;
    bookingAddOns?: Array<IBookingAddOn>;
    bookingDigitalAddOns?: Array<IBookingDigitalAddOn>;
}

export const CLEAR_BOOKING: 'CLEAR_BOOKING' = 'CLEAR_BOOKING';
export const CLEAR_CODE: 'CLEAR_CODE' = 'CLEAR_CODE';

export const RECORD_CODE: 'RECORD_CODE' = 'RECORD_CODE';

export const DISCOUNT_CODE_LOADING: 'DISCOUNT_CODE_LOADING' = 'DISCOUNT_CODE_LOADING';
export const DISCOUNT_CODE_APPLIED: 'DISCOUNT_CODE_APPLIED' = 'DISCOUNT_CODE_APPLIED';
export const DISCOUNT_CODE_REMOVED: 'DISCOUNT_CODE_REMOVED' = 'DISCOUNT_CODE_REMOVED';
export const APPLY_CODE_FAILED: 'APPLY_CODE_FAILED' = 'APPLY_CODE_FAILED';
export const PRESELECT_CODE: 'PRESELECT_CODE' = 'PRESELECT_CODE';

export const CONFIRM_BOOKING_REQUESTED: 'CONFIRM_BOOKING_REQUESTED' = 'CONFIRM_BOOKING_REQUESTED';
export const CONFIRM_BOOKING_SUCCESS: 'CONFIRM_BOOKING_SUCCESS' = 'CONFIRM_BOOKING_SUCCESS';
export const CONFIRM_BOOKING_FAILED: 'CONFIRM_BOOKING_FAILED' = 'CONFIRM_BOOKING_FAILED';
export const CONFIRM_BOOKING_COMPLETE: 'CONFIRM_BOOKING_COMPLETE' = 'CONFIRM_BOOKING_COMPLETE';

export const BOOKING_EXPIRED: 'BOOKING_EXPIRED' = 'BOOKING_EXPIRED';

export const EXPIRE_BOOKING_REQUESTED: 'EXPIRE_BOOKING_REQUESTED' = 'EXPIRE_BOOKING_REQUESTED';
export const EXPIRE_BOOKING_SUCCESS: 'EXPIRE_BOOKING_SUCCESS' = 'EXPIRE_BOOKING_SUCCESS';
export const EXPIRE_BOOKING_FAILED: 'EXPIRE_BOOKING_FAILED' = 'EXPIRE_BOOKING_FAILED';
export const EXPIRE_BOOKING_COMPLETE: 'EXPIRE_BOOKING_COMPLETE' = 'EXPIRE_BOOKING_COMPLETE';

export const UPDATE_BOOKING_REQUESTED: 'UPDATE_BOOKING_REQUESTED' = 'UPDATE_BOOKING_REQUESTED';
export const UPDATE_BOOKING_SUCCESS: 'UPDATE_BOOKING_SUCCESS' = 'UPDATE_BOOKING_SUCCESS';
export const UPDATE_BOOKING_FAILED: 'UPDATE_BOOKING_FAILED' = 'UPDATE_BOOKING_FAILED';
export const UPDATE_BOOKING_COMPLETE: 'UPDATE_BOOKING_COMPLETE' = 'UPDATE_BOOKING_COMPLETE';

export const UPDATE_PAYMENT_OPTION_REQUESTED: 'UPDATE_PAYMENT_OPTION_REQUESTED' = 'UPDATE_PAYMENT_OPTION_REQUESTED';
export const UPDATE_PAYMENT_OPTION_COMPLETE: 'UPDATE_PAYMENT_OPTION_COMPLETE' = 'UPDATE_PAYMENT_OPTION_COMPLETE';
export const UPDATE_PAYMENT_OPTION_FAILED: 'UPDATE_PAYMENT_OPTION_FAILED' = 'UPDATE_PAYMENT_OPTION_FAILED';

export const RESERVE_BOOKING_REQUESTED: 'RESERVE_BOOKING_REQUESTED' = 'RESERVE_BOOKING_REQUESTED';
export const RESERVE_BOOKING_SUCCESS: 'RESERVE_BOOKING_SUCCESS' = 'RESERVE_BOOKING_SUCCESS';
export const RESERVE_BOOKING_FAILED: 'RESERVE_BOOKING_FAILED' = 'RESERVE_BOOKING_FAILED';
export const RESERVE_BOOKING_COMPLETE: 'RESERVE_BOOKING_COMPLETE' = 'RESERVE_BOOKING_COMPLETE';

export const BOOKING_DETAILS_LOADING: 'BOOKING_DETAILS_LOADING' = 'BOOKING_DETAILS_LOADING';
export const BOOKING_DETAILS_LOADED: 'BOOKING_DETAILS_LOADED' = 'BOOKING_DETAILS_LOADED';

export const CHECK_BOOKING_LOADED: 'CHECK_BOOKING_LOADED' = 'CHECK_BOOKING_LOADED';

export const MODIFY_BOOKING: 'MODIFY_BOOKING' = 'MODIFY_BOOKING';

export const UPDATE_PRICE_SETS: 'UPDATE_PRICE_SETS' = 'UPDATE_PRICE_SETS';

export const CHANGE_USER: 'CHANGE_USER' = 'CHANGE_USER';
export const SET_SELECTED_ADDONS: 'SET_SELECTED_ADDONS' = 'SET_SELECTED_ADDONS';
export const UPDATE_ADDONS_REQUESTED: 'UPDATE_ADDONS_REQUESTED' = 'UPDATE_ADDONS_REQUESTED';
export const UPDATE_ADDONS_COMPLETE: 'UPDATE_ADDONS_COMPLETE' = 'UPDATE_ADDONS_COMPLETE';
export const CHARGE_REFRESHED: 'CHARGE_REFRESHED' = 'CHARGE_REFRESHED';

export const UPDATE_SLOTS_REQUESTED: 'UPDATE_SLOTS_REQUESTED' = 'UPDATE_SLOTS_REQUESTED';
export const UPDATE_SLOTS_SUCCESS: 'UPDATE_SLOTS_SUCCESS' = 'UPDATE_SLOTS_SUCCESS';
export const UPDATE_SLOTS_FAILED: 'UPDATE_SLOTS_FAILED' = 'UPDATE_SLOTS_FAILED';

export interface IClearBookingAction extends Action {
    type: typeof CLEAR_BOOKING;
}

export interface IClearCodeAction extends Action {
    type: typeof CLEAR_CODE;
}

export interface IBookingDetailsLoadingAction extends IAnalyticsAction {
    type: typeof BOOKING_DETAILS_LOADING;
}
export interface IBookingDetailsLoadedAction extends IAnalyticsAction {
    type: typeof BOOKING_DETAILS_LOADED;
    details?: IBookingDetails;
    success: boolean;
}

export interface ICheckBookingLoadedAction extends Action {
    type: typeof CHECK_BOOKING_LOADED;
    details?: IBookingDetails;
    success: boolean;
}

export interface IModifyBookingAction extends IAnalyticsAction {
    type: typeof MODIFY_BOOKING;
}

export interface IExpireBookingRequestedAction extends Action {
    type: typeof EXPIRE_BOOKING_REQUESTED;
}
export interface IExpireBookingSuccessAction extends Action {
    type: typeof EXPIRE_BOOKING_SUCCESS;
}
export interface IExpireBookingFailedAction extends Action {
    type: typeof EXPIRE_BOOKING_FAILED;
}
export interface IExpireBookingCompletedAction extends Action {
    type: typeof EXPIRE_BOOKING_COMPLETE;
}

export interface IExpiredBookingAction extends IAnalyticsAction {
    type: typeof BOOKING_EXPIRED;
    bookingRef: string;
    bookingId: number;
}

export interface IReserveBookingRequestedAction extends Action {
    type: typeof RESERVE_BOOKING_REQUESTED;
}

export interface IReserveBookingSuccessAction extends IAnalyticsAction {
    type: typeof RESERVE_BOOKING_SUCCESS;
    booking: IBooking;
    charge: ICharge;
    appliedCodes: Array<IDiscountCode>;
    availableDigitalAddOns?: Array<IAvailableDigitalAddOn>;
    availableAddOns?: Array<IAvailableAddOn>;
    selectedDigitalAddOns?: Array<IUpdateBookingDigitalAddOn>;
    selectedAddOns?: Array<IUpdateBookingAddOn>;
}

export interface IReserveBookingFailedAction extends Action {
    type: typeof RESERVE_BOOKING_FAILED;
    failureMessageKey: string;
}

export interface IReserveBookingCompletedAction extends Action {
    type: typeof RESERVE_BOOKING_COMPLETE;
}

export interface IUpdateBookingRequestedAction extends Action {
    type: typeof UPDATE_BOOKING_REQUESTED;
    clientSecret?: string;
}

export interface IUpdateBookingSuccessAction extends IAnalyticsAction {
    type: typeof UPDATE_BOOKING_SUCCESS;
}

export interface IUpdateBookingFailedAction extends Action {
    type: typeof UPDATE_BOOKING_FAILED;
    failureMessageKey: string;
}

export interface IUpdateBookingCompletedAction extends Action {
    type: typeof UPDATE_BOOKING_COMPLETE;
}

export interface IConfirmBookingRequestedAction extends Action {
    type: typeof CONFIRM_BOOKING_REQUESTED;
}

export interface IConfirmBookingSuccessAction extends IAnalyticsAction {
    type: typeof CONFIRM_BOOKING_SUCCESS;
    reserved: boolean;
}

export interface IConfirmBookingFailedAction extends Action {
    type: typeof CONFIRM_BOOKING_FAILED;
}

export interface IConfirmBookingCompletedAction extends Action {
    type: typeof CONFIRM_BOOKING_COMPLETE;
}

export interface IRecordCodeAction extends Action {
    type: typeof RECORD_CODE;
    code: string;
}

export interface IApplyCodeFailed extends IAnalyticsAction {
    type: typeof APPLY_CODE_FAILED;
    codeResponse: DiscountCodeResponseType;
}

export interface IDiscountCodeApplied extends IAnalyticsAction {
    type: typeof DISCOUNT_CODE_APPLIED;
    booking: IBooking;
    charge: ICharge;
    codes: Array<IDiscountCode>;
}

export interface IDiscountCodeRemoved extends IAnalyticsAction {
    type: typeof DISCOUNT_CODE_REMOVED;
    booking: IBooking;
    charge: ICharge;
    codes: Array<IDiscountCode>;
}

export interface IDiscountCodeLoading extends Action {
    type: typeof DISCOUNT_CODE_LOADING;
}

export interface IPreSelectPromoAction extends Action {
    type: typeof PRESELECT_CODE;
    code: string;
}

export interface IUpdatePriceSetsAction extends IAnalyticsAction {
    type: typeof UPDATE_PRICE_SETS;
    priceSets: Array<IPriceSet>;
}

export interface IChangeUserAction extends IAnalyticsAction {
    type: typeof CHANGE_USER;
    booking: IBooking;
}

export interface ISetSelectedAddOnsAction extends IAnalyticsAction {
    type: typeof SET_SELECTED_ADDONS;
    selectedAddOns: IUpdateBookingAddOn[] | undefined;
    selectedDigitalAddOns: IUpdateBookingDigitalAddOn[] | undefined;
}

export interface IUpdateAddOnsRequestedAction extends IAnalyticsAction {
    type: typeof UPDATE_ADDONS_REQUESTED;
}

export interface IUpdateAddOnsCompleteAction extends IAnalyticsAction {
    type: typeof UPDATE_ADDONS_COMPLETE;
    bookingAddOns: IBookingAddOn[];
    bookingDigitalAddOns: IBookingDigitalAddOn[];
    charge: ICharge | undefined;
}

interface IChargeRefreshedAction extends Action {
    type: typeof CHARGE_REFRESHED;
    charge: ICharge;
}

export interface IUpdatePaymentOptionRequestedAction extends Action {
    type: typeof UPDATE_PAYMENT_OPTION_REQUESTED;
}

export interface IUpdatePaymentOptionCompleteAction extends IAnalyticsAction {
    type: typeof UPDATE_PAYMENT_OPTION_COMPLETE;
    charge: ICharge | undefined;
}

export interface IUpdatePaymentOptionFailedAction extends Action {
    type: typeof UPDATE_PAYMENT_OPTION_FAILED;
}

export interface IUpdateSlotsRequestedAction extends Action {
    type: typeof UPDATE_SLOTS_REQUESTED;
}

export interface IUpdateSlotsSuccessAction extends Action {
    type: typeof UPDATE_SLOTS_SUCCESS;
    booking: IBooking;
    charge: ICharge;
    appliedCodes: Array<IDiscountCode>;
    availableDigitalAddOns?: Array<IAvailableDigitalAddOn>;
    availableAddOns?: Array<IAvailableAddOn>;
}

export interface IUpdateSlotsFailedAction extends Action {
    type: typeof UPDATE_SLOTS_FAILED;
    failureMessageKey: string;
}

export type KnownBookingActions =
    | IReserveBookingRequestedAction
    | IReserveBookingSuccessAction
    | IReserveBookingFailedAction
    | IReserveBookingCompletedAction
    | IUpdateBookingRequestedAction
    | IUpdateBookingSuccessAction
    | IUpdateBookingFailedAction
    | IUpdateBookingCompletedAction
    | IConfirmBookingRequestedAction
    | IConfirmBookingSuccessAction
    | IConfirmBookingFailedAction
    | IConfirmBookingCompletedAction
    | IExpiredBookingAction
    | IExpireBookingRequestedAction
    | IExpireBookingSuccessAction
    | IExpireBookingFailedAction
    | IExpireBookingCompletedAction
    | IPreSelectPromoAction
    | IRecordCodeAction
    | IApplyCodeFailed
    | IDiscountCodeApplied
    | IDiscountCodeRemoved
    | IDiscountCodeLoading
    | IModifyBookingAction
    | IClearBookingAction
    | IClearCodeAction
    | IBookingDetailsLoadedAction
    | IBookingDetailsLoadingAction
    | CallHistoryMethodAction
    | ICheckBookingLoadedAction
    | IUpdatePriceSetsAction
    | IChangeUserAction
    | ISetSelectedAddOnsAction
    | IUpdateAddOnsRequestedAction
    | IUpdateAddOnsCompleteAction
    | IChargeRefreshedAction
    | IUpdatePaymentOptionRequestedAction
    | IUpdatePaymentOptionCompleteAction
    | IUpdatePaymentOptionFailedAction
    | IUpdateSlotsRequestedAction
    | IUpdateSlotsSuccessAction
    | IUpdateSlotsFailedAction;

let inFlightUpdateBooking: AbortController | null = null;
let updatePaymentOptionAbortController: AbortController | null = null;

export const actionCreators = {
    continuePendingBooking: (): IAppThunkAction<KnownBookingActions> => (dispatch, getState) => {
        const appState = getState();
        const session = appState.session.selectedSession;
        const booking = appState.booking.booking;
        const isEventBooking = appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.PrivateEvent;
        const charge = appState.booking.charge;
        const selectedSite = appState.sites.selectedSite;
        const currentLocale = appState.intl.selectedLanguage.code;
        const packageName = session?.package.alias ?? 'N/A';
        const availableAddOns = appState.booking.availableAddOns;
        const availableDigitalAddOns = appState.booking.availableDigitalAddOns;

        if((availableAddOns && availableAddOns.length > 0) ||
            (availableDigitalAddOns && availableDigitalAddOns.length > 0)) {
            dispatch(navigateTo(Routes.Addons, appState.sites.selectedSite?.urlKey));
        } else {
            dispatch(navigateTo(Routes.Checkout, appState.sites.selectedSite?.urlKey));

            if (charge && booking && selectedSite) {
                const isReservation = appState?.sites?.selectedSite?.reservationPortal ?? false;
                const isGiftVoucher = appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GiftVoucher;

                BrazeEvents.CheckoutInitiated(
                    isEventBooking,
                    charge,
                    selectedSite,
                    currentLocale,
                    isReservation,
                    isGiftVoucher,
                    packageName,
                    booking
                );
            }
        }
    },

    modifyBooking:
        (booking: IBooking): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState = getState();

            dispatch({
                type: MODIFY_BOOKING,
                googleTagManagerEvent: GoogleTagManagerEvents.ModifiedBooking(booking),
                brazeEvent: BrazeEvents.ModifyCart(),
            } as IModifyBookingAction);

            if (appState.purchaseType.selectedPurchaseType === PurchaseTypes.Booking) {
                dispatch(navigateTo(Routes.BookNow, appState.sites.selectedSite?.urlKey));
            } else {
                dispatch(navigateTo(Routes.PrivateEvent, appState.sites.selectedSite?.urlKey));
            }
        },

    getBookingDetails:
        (bookingRef: string): IAppThunkAction<KnownBookingActions> =>
        (dispatch, _getState) => {
            if (bookingRef) {
                BookingService.GetDetails(bookingRef)
                    .then((data) => {
                        dispatch({
                            type: BOOKING_DETAILS_LOADED,
                            success: true,
                            details: data,
                        });
                    })
                    .catch((exception) => {
                        dispatch({
                            type: BOOKING_DETAILS_LOADED,
                            success: false,
                            details: undefined,
                        });

                         appInsights?.trackException(
                             { error: new Error(`Error getting booking details`), severityLevel: 2},
                             { exception: exception, sourceFile: "Booking.tsx", product: "booking" });
                    });
            }

            dispatch({ type: BOOKING_DETAILS_LOADING });
        },

    getSessionIdForBooking: (siteId: number, bookingRef: string): Promise<number | undefined> => {
        if (siteId && bookingRef) {
            return BookingService.GetSessionIdForBooking(siteId, bookingRef);
        }
        return Promise.resolve(undefined);
    },

    checkBookingExpired:
        (bookingRef: string): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            if (bookingRef) {
                BookingService.GetDetails(bookingRef)
                    .then((data) => {
                        const appState = getState();
                        if (!_.isEqual(appState.booking.details, data)) {
                            dispatch({
                                type: CHECK_BOOKING_LOADED,
                                success: true,
                                details: data,
                            });
                        }
                    })
                    .catch((exception) => {
                        dispatch({
                            type: CHECK_BOOKING_LOADED,
                            success: false,
                            details: undefined,
                        });

                         appInsights?.trackException(
                             { error: new Error(`Error checking booking expired`), severityLevel: 2},
                             { exception: exception, sourceFile: "Booking.tsx", product: "booking" });
                    });
            }
        },

    bookingExpired: (bookingRef: string, bookingId: number) =>
        ({
            type: BOOKING_EXPIRED,
            bookingRef: bookingRef,
            bookingId: bookingId,
            googleTagManagerEvent: GoogleTagManagerEvents.BookingExpired(bookingRef, bookingId),
        } as IExpiredBookingAction),

    validateBooking:
        (onValidationSuccess: any, onValidationFailed: any): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            const booking = appState.booking.booking;
            if (booking) {
                BookingService.Validate(booking.bookingRef)
                    .then((data) => {
                        if (data.bookingStatusTypeId !== 5) {
                            onValidationSuccess();
                        } else {
                            onValidationFailed();
                        }
                        dispatch({
                            type: BOOKING_DETAILS_LOADED,
                            success: true,
                            details: data,
                        });
                    })
                    .catch((exception) => {
                        onValidationFailed();
                         appInsights?.trackException(
                             { error: new Error(`Error on validate booking`), severityLevel: 2},
                             { exception: exception, sourceFile: "Booking.tsx", product: "booking" });
                    });
            }
        },

    reserveBooking:
        (session: Session, adBlockEnabled: boolean): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            const session = appState.session.selectedSession; //ui selection
            const playerCount = appState.players.selectedPlayers; //ui selection
            const isEventBooking = appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.PrivateEvent; //ui selection
            const privateEventType = appState.players.selectedPrivateEventTypeId ?? 1; // Default value is one for this UI
            const isPrivateSession =
                appState.players.selectedPrivateUpgrade && (session?.package.privacyTypeId ?? 0) > 0;
            const priceSet = appState.booking.priceSets?.find((p) => p.venue === appState.sites.selectedSite?.urlKey);
            const packageName = session?.package.alias ?? 'N/A';

            const selectedSite = appState.sites.selectedSite;
            const currentLocale = appState.intl.selectedLanguage.code;

            const isReservation = appState?.sites?.selectedSite?.reservationPortal ?? false;
            const isGiftVoucher = appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GiftVoucher ||
                appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher;

            if (session && playerCount && selectedSite) {
                const emailAddress = appState.customer?.emailAddress;
                const discountCode = appState.booking.preSelectedCode;

                // Build reward flow data from upsell state to pass through the booking request
                const rewardsState = appState.rewards;
                let rewardFlowData: IRewardFlowRequestData[] | undefined;
                if (rewardsState.upsellWasShown) {
                    const upsellFlow = new PlayerCountUpsellFlow(rewardsState.flows);
                    const matchingFlow = rewardsState.flows.find((f) => f.alias === PlayerCountUpsellKeys.FlowAlias);
                    if (matchingFlow) {
                        rewardFlowData = [{
                            rewardFlowId: matchingFlow.rewardFlowId,
                            data: {
                                [PlayerCountUpsellKeys.WasOfferShown]: 'true',
                                [PlayerCountUpsellKeys.WasOfferAccepted]: upsellFlow.isOfferMet(playerCount) ? 'true' : 'false',
                            },
                        }];
                    }
                }

                BookingService.ReserveBooking(
                    session,
                    playerCount,
                    emailAddress,
                    discountCode,
                    adBlockEnabled,
                    privateEventType,
                    isPrivateSession,
                    priceSet?.code,
                    rewardFlowData
                )
                    .then((data) => {
                        console.log();
                        dispatch({
                            type: RESERVE_BOOKING_SUCCESS,
                            booking: data.booking,
                            charge: data.charge,
                            appliedCodes: data.discountCode,
                            availableDigitalAddOns: data.availableDigitalAddOns,
                            availableAddOns: data.availableAddOns,
                            selectedDigitalAddOns: appState.booking.selectedDigitalAddOns?.filter(a => data.availableDigitalAddOns?.find(b => b.digitalAddOnOptionId === a.digitalAddOnOptionId) !== undefined),
                            selectedAddOns: appState.booking.selectedAddOns?.filter(a => data.availableAddOns?.find(b => b.options.find(c => c.addOnOptionId === a.addOnOptionId) !== undefined) !== undefined),
                            googleTagManagerEvent: GoogleTagManagerEvents.ReserveBooking(data.charge),
                            brazeEvent: BrazeEvents.CheckoutInitiated(
                                isEventBooking,
                                data.charge,
                                selectedSite,
                                currentLocale,
                                isReservation,
                                isGiftVoucher,
                                packageName,
                                data.booking
                            ),
                        });
                        Logging.SetInvoiceId(data.booking.invoiceId?.toString());

                        if((data.availableAddOns && data.availableAddOns.length > 0) ||
                            (data.availableDigitalAddOns && data.availableDigitalAddOns.length > 0)) {
                            dispatch(navigateTo(Routes.Addons, appState.sites.selectedSite?.urlKey));

                        } else {
                            dispatch(navigateTo(Routes.Checkout, appState.sites.selectedSite?.urlKey));
                        }
                    })

                    .catch((error: Error) => {
                        dispatch({
                            type: RESERVE_BOOKING_FAILED,
                            failureMessageKey: 'checkout failed',
                        });
                         appInsights?.trackException(
                             { error: new Error(`Error reserving booking`), severityLevel: 2},
                             { exception: error, sourceFile: "Booking.tsx", product: "booking" });
                    });

                dispatch({ type: RESERVE_BOOKING_REQUESTED });
            }
        },

    confirmBooking:
        (
            paymentGatewayMetadata: any,
            paymentMethodReference: string,
            paymentMethodTypeId: number,
            adBlockDetected: boolean
        ): IAppThunkAction<any> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();

            if (appState && appState.booking.booking && appState.booking.charge && appState.customer?.emailAddress) {
                const booking = appState.booking.booking;
                const charge = appState.booking.charge;
                const emailAddress = appState.customer.emailAddress;
                const fullName = appState.customer?.fullName;
                const phoneNumber = formatPhoneNumber(appState.customer?.callingCode, appState.customer?.phoneNumber);

                // Compute effective payment amount based on payment option selection
                const effectiveCharge = getEffectiveCharge(
                    charge,
                    appState.paymentOption.selectedOption,
                    booking.initialSlotCount,
                    appState.paymentOption.splitSlotCount
                );
                const overrideAmount = effectiveCharge.total !== charge.total ? effectiveCharge.total : undefined;

                //move to processing page before starting
                dispatch(navigateTo(Routes.Processing));

                BookingService.ConfirmBooking(
                    booking,
                    charge,
                    emailAddress,
                    fullName,
                    phoneNumber,
                    paymentMethodReference,
                    paymentMethodTypeId,
                    paymentGatewayMetadata,
                    adBlockDetected,
                    overrideAmount
                )
                    .then((_data) => {
                        // Confirm API succeeded. The reservation timer will poll GetDetails,
                        // detect the Active/Reserved status, and call confirmBookingSuccess()
                        // to navigate to /complete.
                    })
                    .catch((error: Error) => {
                        dispatch({ type: CONFIRM_BOOKING_FAILED });
                         appInsights?.trackException(
                             { error: new Error(`Error confirming booking`), severityLevel: 2},
                             { exception: error, sourceFile: "Booking.tsx", product: "booking" });
                    });

                dispatch({
                    type: CONFIRM_BOOKING_COMPLETE,
                });
            }

            dispatch({ type: CONFIRM_BOOKING_REQUESTED });
        },

    confirmBookingSuccess: (): IAppThunkAction<KnownBookingActions> => (dispatch, getState) => { // Complete Booking redirect
        const appState: IApplicationState = getState();
        const booking = appState.booking.booking;
        const charge = appState.booking.charge;
        const session = appState.session.selectedSession;

        if (session && charge && booking) {
            const siteCode = appState.sites.selectedSite?.code ?? '';
            const gtmKey = appState.sites.selectedSiteGtmKey;
            const appliedCodes = appState.booking.appliedCodes;

            dispatch({
                type: CONFIRM_BOOKING_SUCCESS,
                reserved: appState.booking.status === BookingStatus.Reserved,
                googleTagManagerEvent: GoogleTagManagerEvents.ConfirmBooking(
                    charge,
                    session,
                    booking,
                    siteCode,
                    appliedCodes,
                    gtmKey
                ),
            });

            dispatch(navigateTo(Routes.Complete, appState.sites.selectedSite?.urlKey));
        }
    },

    expireBooking: (): IAppThunkAction<KnownBookingActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();
        const booking = appState.booking.booking;
        const status = appState.booking.status;
        if (booking && status === BookingStatus.Pending) {
            BookingService.ExpireBooking(booking)
                .then((_response) => {
                    dispatch({ type: EXPIRE_BOOKING_SUCCESS });
                    dispatch({ type: EXPIRE_BOOKING_COMPLETE });
                })

                .catch((error: Error) => {
                    dispatch({ type: EXPIRE_BOOKING_FAILED });
                    dispatch({ type: EXPIRE_BOOKING_COMPLETE });
                     appInsights?.trackException(
                         { error: new Error(`Error expiring booking`), severityLevel: 2},
                         { exception: error, sourceFile: "Booking.tsx", product: "booking" });
                });
        }

        dispatch({ type: EXPIRE_BOOKING_REQUESTED });
    },

    clearBooking: (): IAppThunkAction<KnownBookingActions> => (dispatch) => {
        Logging.SetInvoiceId('');
        dispatch({ type: CLEAR_BOOKING });
    },

    recordPreselectedCode:
        (code: string | null): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            if (code !== null && code !== '' && appState.booking.preSelectedCode !== code)
                dispatch({ type: PRESELECT_CODE, code: code });
        },

    recordCode:
        (code: string, email: string): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            if (appState.booking.booking) {
                const booking = appState.booking.booking;
                BookingService.ApplyCode(booking, code, email)
                    .then((data) => {
                        const codeResponse = data.discountCodeResponseType as DiscountCodeResponseType;
                        if (
                            codeResponse === DiscountCodeResponseType.Available &&
                            data.booking &&
                            data.charge &&
                            data.discountCode
                        ) {
                            dispatch({
                                type: DISCOUNT_CODE_APPLIED,
                                booking: data.booking,
                                charge: data.charge,
                                codes: data.discountCode,
                                googleTagManagerEvent: GoogleTagManagerEvents.AppliedDiscountCode(code),
                                brazeEvent: BrazeEvents.DiscountAdded(data.discountCode),
                            });
                            // The applycode endpoint recomputes the charge without the organiser
                            // slot count (it defaults to 1 server-side), so a gift voucher can zero
                            // out splitPaymentTotal. Re-sync via the update endpoint, which sends
                            // organizerSlotCount, so the split total is correct for the selected count.
                            if (getState().paymentOption.selectedOption === 'split') {
                                (dispatch as any)(actionCreators.updatePaymentOptionOnServer());
                            }
                        } else {
                            dispatch({
                                type: APPLY_CODE_FAILED,
                                googleTagManagerEvent: GoogleTagManagerEvents.InvalidDiscountCode(code),
                                codeResponse: codeResponse,
                            });
                        }
                    })
                    .catch((error: Error) => {
                        dispatch({
                            type: APPLY_CODE_FAILED,
                            googleTagManagerEvent: GoogleTagManagerEvents.InvalidDiscountCode(code),
                            codeResponse: DiscountCodeResponseType.NotFound,
                        });
                         appInsights?.trackException(
                             { error: new Error(`Error applying code to booking`), severityLevel: 2},
                             { exception: error, sourceFile: "Booking.tsx", product: "booking" });
                    });

                dispatch({
                    type: DISCOUNT_CODE_LOADING,
                });
            }
        },

    removeCode:
        (code: string): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            if (appState.booking.booking) {
                const booking = appState.booking.booking;
                BookingService.RemoveCode(booking, code).then((data) => {
                    dispatch({
                        type: DISCOUNT_CODE_REMOVED,
                        booking: data.booking,
                        charge: data.charge,
                        codes: data.discountCode ?? [],
                        googleTagManagerEvent: GoogleTagManagerEvents.RemovedDiscountCode(code),
                    });
                    // The removecode endpoint recomputes the charge without the organiser slot
                    // count (defaults to 1 server-side). Re-sync via the update endpoint, which
                    // sends organizerSlotCount, so the split total is correct for the selected count.
                    if (getState().paymentOption.selectedOption === 'split') {
                        (dispatch as any)(actionCreators.updatePaymentOptionOnServer());
                    }
                });

                dispatch({
                    type: DISCOUNT_CODE_LOADING,
                });
            }
        },

    removeAddOn:
        (addOnId: number): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            if (appState.booking.booking) {
                const booking = appState.booking.booking;
                const selectedAddOns = appState.booking.selectedAddOns?.filter((a => a.addOnOptionId !== addOnId));
                const selectedDigitalAddOns = appState.booking.selectedDigitalAddOns;
                dispatch({ type: SET_SELECTED_ADDONS, selectedAddOns: selectedAddOns, selectedDigitalAddOns: selectedDigitalAddOns });

                if(appState.booking.bookingAddOns?.find(a => a.addOnOptionId === addOnId)) {
                    dispatch({ type: UPDATE_BOOKING_REQUESTED });

                    BookingService.ApplyAddOns(booking, selectedAddOns, selectedDigitalAddOns).then((data) => {
                        dispatch({
                            type: UPDATE_ADDONS_COMPLETE,
                            bookingAddOns: data.bookingAddOns ?? [],
                            bookingDigitalAddOns: data.bookingDigitalAddOns ?? [],
                            charge: data.charge ?? undefined,
                        });
                    });
                    
                    dispatch({ type: UPDATE_ADDONS_REQUESTED });
                }
            }
        },

    removeDigitalAddOn:
        (digitalAddOnOptionId: number): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            if (appState.booking.booking) {
                const booking = appState.booking.booking;
                const selectedAddOns = appState.booking.selectedAddOns;
                const selectedDigitalAddOns = appState.booking.selectedDigitalAddOns?.filter((a => a.digitalAddOnOptionId !== digitalAddOnOptionId));
                dispatch({ type: SET_SELECTED_ADDONS, selectedAddOns: selectedAddOns, selectedDigitalAddOns: selectedDigitalAddOns });

                if(appState.booking.bookingDigitalAddOns?.find(a => a.digitalAddOnOptionId === digitalAddOnOptionId)) {
                    dispatch({ type: UPDATE_BOOKING_REQUESTED });

                    BookingService.ApplyAddOns(booking, selectedAddOns, selectedDigitalAddOns).then((data) => {
                        dispatch({
                            type: UPDATE_ADDONS_COMPLETE,
                            bookingAddOns: data.bookingAddOns ?? [],
                            bookingDigitalAddOns: data.bookingDigitalAddOns ?? [],
                            charge: data.charge ?? undefined,
                        });
                    });
                    
                    dispatch({ type: UPDATE_ADDONS_REQUESTED });
                }
            }
        },

    clearCode: () => ({ type: CLEAR_CODE } as IClearCodeAction),

    updateBooking: (): IAppThunkAction<KnownBookingActions> => (dispatch, getState) => {

        // cancel previous request if one is running
        if (inFlightUpdateBooking) inFlightUpdateBooking.abort("updateBooking called with newer state");

        // create per-call controller and hand over as current
        const controller = new AbortController();
        inFlightUpdateBooking = controller;

        const appState: IApplicationState = getState();

        // Customer data is read from the Customer store — this booking thunk currently serves
        // as the sync mechanism for customer details. See Customer.ts header for migration plan.
        const emailAddress = appState.customer?.emailAddress;
        const fullName = appState.customer?.fullName;
        const phoneNumber = formatPhoneNumber(appState.customer?.callingCode, appState.customer?.phoneNumber);

        const bookingCode = appState.booking.booking?.code ?? appState.booking.preSelectedCode;

        const venueId = appState?.sites?.selectedSite?.siteId;

        const currentLocale = appState.intl.selectedLanguage.code;

        if (appState && appState.booking.booking && emailAddress && venueId) {
            dispatch({ type: UPDATE_BOOKING_REQUESTED });
            const booking = appState.booking.booking;

            BookingService.UpdateBooking(booking, emailAddress, fullName, phoneNumber ?? null, bookingCode, undefined, controller.signal, currentLocale)
                .then((data) => {
                    if (bookingCode && data.booking && data.charge && data.discountCode.length > 0) {
                        dispatch({
                            type: DISCOUNT_CODE_APPLIED,
                            booking: data.booking,
                            charge: data.charge,
                            codes: data.discountCode,
                            googleTagManagerEvent: GoogleTagManagerEvents.AppliedDiscountCode(bookingCode),
                            brazeEvent: BrazeEvents.DiscountAdded(data.discountCode[0]),
                        });
                    }

                    dispatch({ type: UPDATE_BOOKING_COMPLETE });
                    // TODO: When customer sync gets its own API endpoint, move this markSynced()
                    // call into a dedicated customer thunk instead of piggybacking on the booking update.
                    dispatch(CustomerActions.markSynced() as any);
                })
                .catch((error: Error) => {
                    if ((error as any)?.name === 'AbortError') {
                        console.log("ignore cancelled api request in updateBooking", error);
                        return; // superseded by a newer call, that call manages the inFlight flag
                    }

                    dispatch({ type: UPDATE_BOOKING_FAILED, failureMessageKey: 'errors.updateBookingFailed' });
                    appInsights?.trackException(
                        { error: new Error(`Error updating booking`), severityLevel: 2 },
                        { exception: error, sourceFile: "Booking.ts", product: "booking" });
                })
                .finally(() => {
                    // only clear if this request is still the active one
                    if (inFlightUpdateBooking === controller) inFlightUpdateBooking = null;
                });
        }
    },

    changeUser: (): IAppThunkAction<KnownBookingActions> => (dispatch, getState) => {
        // cancel previous booking compound update if one is running (shared with updateBooking)
        if (inFlightUpdateBooking) inFlightUpdateBooking.abort("changeUser called with newer state");

        const controller = new AbortController();
        inFlightUpdateBooking = controller;

        const appState: IApplicationState = getState();

        // Customer data read from Customer store — see Customer.ts header for migration plan
        const emailAddress = appState.customer?.emailAddress;
        const fullName = appState.customer?.fullName;
        const phoneNumber = formatPhoneNumber(appState.customer?.callingCode, appState.customer?.phoneNumber);

        const venueId = appState?.sites?.selectedSite?.siteId;
        const venueName = appState?.sites?.selectedSite?.name ?? 'N/A';
        const names = fullName?.split(' ') ?? [];
        const firstName = names[0] ?? '';
        const lastName = names?.slice(1).join(' ') ?? '';

        const bookingCode = appState.booking.booking?.code ?? appState.booking.preSelectedCode;

        const selectedSite = appState.sites.selectedSite;

        const currentLocale = appState.intl.selectedLanguage.code;

        if (appState && appState.booking.booking && emailAddress && venueId && selectedSite) {
            const booking = appState.booking.booking;

            dispatch({ type: UPDATE_BOOKING_REQUESTED });

            console.log(`Submitting Discount Code: ${bookingCode}`);
            BookingService.UpdateBooking(booking, emailAddress, fullName, phoneNumber ?? null, bookingCode, undefined, controller.signal, currentLocale).then((data) => {
                dispatch({
                    type: CHANGE_USER,
                    booking: data.booking,
                    brazeEvent: BrazeEvents.ChangeUser(
                        data.booking.playerGuid,
                        emailAddress,
                        firstName,
                        lastName,
                        venueId,
                        venueName,
                        phoneNumber ?? null,
                        selectedSite,
                        currentLocale
                    ),
                });

                console.log(`Discount Code: ${data.discountCode}`);
                if (bookingCode && data.booking && data.charge && data.discountCode.length > 0) {
                    console.log('Discount Code Added');
                    dispatch({
                        type: DISCOUNT_CODE_APPLIED,
                        booking: data.booking,
                        charge: data.charge,
                        codes: data.discountCode,
                        googleTagManagerEvent: GoogleTagManagerEvents.AppliedDiscountCode(bookingCode),
                        brazeEvent: BrazeEvents.DiscountAdded(data.discountCode[0]),
                    });
                }

                dispatch({ type: UPDATE_BOOKING_COMPLETE });
                dispatch(CustomerActions.markSynced() as any);
            }).catch((error: Error) => {
                if ((error as any)?.name === 'AbortError') {
                    return; // superseded by a newer call, that call manages the inFlight flag
                }

                dispatch({ type: UPDATE_BOOKING_FAILED, failureMessageKey: 'errors.updateBookingFailed' });
                appInsights?.trackException(
                    { error: new Error(`Error in changeUser`), severityLevel: 2 },
                    { exception: error, sourceFile: 'Booking.ts', product: 'booking' });
            }).finally(() => {
                if (inFlightUpdateBooking === controller) inFlightUpdateBooking = null;
            });
        }
    },

    recordPriceSet:
        (venue: string, setCode: string | null, source: string): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
        if (!setCode) return;

        const appState: IApplicationState = getState();

        let newPriceSets = appState.booking.priceSets?.filter((p) => p.venue !== venue);

        if (!newPriceSets) newPriceSets = [];

        newPriceSets.push({ venue: venue, code: setCode });

        dispatch({
            type: UPDATE_PRICE_SETS,
            priceSets: newPriceSets,
            googleTagManagerEvent: GoogleTagManagerEvents.RecordPriceSet(venue, setCode, source),
        });
    },

    setSelectedAddOns: (addOns: IUpdateBookingAddOn[] | undefined, digitalAddOns: IUpdateBookingDigitalAddOn[] | undefined): IAppThunkAction<KnownBookingActions> => (dispatch) => {
        dispatch({
            type: SET_SELECTED_ADDONS,
            selectedAddOns: addOns,
            selectedDigitalAddOns: digitalAddOns,
        });
    },

    applyAddOns: (): IAppThunkAction<KnownBookingActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();

        const booking = appState.booking.booking;
        const selectedAddOns = appState.booking.selectedAddOns;
        const selectedDigitalAddOns = appState.booking.selectedDigitalAddOns;

        if (booking && (
            (selectedAddOns && selectedAddOns.length > 0) || 
            (selectedDigitalAddOns && selectedDigitalAddOns.length > 0))) {

            dispatch({ type: UPDATE_BOOKING_REQUESTED });

            BookingService.ApplyAddOns(booking, selectedAddOns, selectedDigitalAddOns).then((data) => {
                dispatch({
                    type: UPDATE_ADDONS_COMPLETE,
                    bookingAddOns: data.bookingAddOns ?? [],
                    bookingDigitalAddOns: data.bookingDigitalAddOns ?? [],
                    charge: data.charge ?? undefined,
                });
                dispatch(navigateTo(Routes.Checkout, appState.sites.selectedSite?.urlKey));
            });

            dispatch({ type: UPDATE_ADDONS_REQUESTED });
        } else {
            dispatch(navigateTo(Routes.Checkout, appState.sites.selectedSite?.urlKey));
        }
    },

    refreshChargeForPaymentMethod:
        (paymentMethodTypeId: number, signal?: AbortSignal): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            const booking = appState.booking.booking;
            if (!booking || !appState.booking.charge) return Promise.resolve();

            return BookingService.UpdateBooking(
                booking,
                appState.customer?.emailAddress ?? '',
                appState.customer?.fullName,
                formatPhoneNumber(appState.customer?.callingCode, appState.customer?.phoneNumber) ?? null,
                undefined,
                paymentMethodTypeId,
                signal
            ).then((data: IPaymentRequest) => {
                dispatch({ type: CHARGE_REFRESHED, charge: data.charge });
            }).catch((error: unknown) => {
                if (error instanceof DOMException && error.name === 'AbortError') return;
                if((error as any)?.name === 'AbortError') return;
                throw error;
            });
        },

    updatePaymentOptionOnServer:
        (): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();
            const booking = appState.booking.booking;
            const { selectedOption, splitSlotCount } = appState.paymentOption;

            if (!booking?.siteId || !booking?.bookingRef) return;

            if (updatePaymentOptionAbortController) {
                updatePaymentOptionAbortController.abort();
            }
            updatePaymentOptionAbortController = new AbortController();

            // Defence-in-depth: gate payment submission while the request is in flight.
            // The caller in payment-option-section.tsx also dispatches this (earlier, before
            // the 500ms debounce) so the full race window is covered.
            dispatch({ type: UPDATE_PAYMENT_OPTION_REQUESTED });

            const paymentOptionTypeId = paymentOptionTypeIdMap[selectedOption];
            const organizerSlotCount = selectedOption === 'split' ? splitSlotCount : undefined;

            BookingService.UpdatePaymentOption(
                booking,
                paymentOptionTypeId,
                organizerSlotCount,
                appState.customer?.emailAddress ?? '',
                appState.customer?.fullName,
                formatPhoneNumber(appState.customer?.callingCode, appState.customer?.phoneNumber) ?? null,
                updatePaymentOptionAbortController.signal,
            )
            .then((data) => {
                // Dispatch an action to indicate the update is complete
                dispatch({ type: UPDATE_PAYMENT_OPTION_COMPLETE,
                    charge: data.charge
                 });
            })
                .catch((error: Error) => {
                    if (error.name === 'AbortError') return;
                    appInsights?.trackException(
                        { error: new Error('Error updating payment option'), severityLevel: 2 },
                        { exception: error, sourceFile: 'PaymentOption.ts', product: 'booking' }
                    );
                    // Clear the gating flag so the user isn't stranded, and surface the error.
                    // Cast to any because IAppThunkAction<KnownBookingActions> constrains dispatch
                    // to booking actions only; the displayError thunk targets KnownUIActions.
                    dispatch({ type: UPDATE_PAYMENT_OPTION_FAILED });
                    (dispatch as any)(UIActions.displayError('errors.payments.error'));
                });
        },

    updateBookingSlots:
        (newPlayerCount: number): IAppThunkAction<KnownBookingActions> =>
        (dispatch, getState) => {
            const appState = getState();
            const booking = appState.booking.booking;
            const siteId = booking?.siteId;

            if (!booking || !siteId) return;

            dispatch({ type: UPDATE_SLOTS_REQUESTED });

            PlayersService.UpdateSelectedTickets(booking.bookingRef, siteId, newPlayerCount)
                .then((data) => {
                    dispatch({
                        type: UPDATE_SLOTS_SUCCESS,
                        booking: data.booking,
                        charge: data.charge,
                        appliedCodes: data.discountCode,
                        availableDigitalAddOns: data.availableDigitalAddOns,
                        availableAddOns: data.availableAddOns,
                    });
                    // Sync the Redux player count now that the server accepted the change
                    dispatch({ type: PLAYERS_SELECTED, players: newPlayerCount } as any);
                })
                .catch((error: Error) => {
                    console.error('[updateBookingSlots] Failed to update ticket count', error);
                    appInsights?.trackException(
                        { error: new Error('Failed to update booking slots'), severityLevel: 2 },
                        { exception: error, sourceFile: 'Booking.ts', product: 'booking' }
                    );
                    // Revert player count to match the server-side booking
                    const currentBooking = getState().booking.booking;
                    if (currentBooking) {
                        dispatch({ type: PLAYERS_SELECTED, players: currentBooking.initialSlotCount } as any);
                    }
                    dispatch({ type: UPDATE_SLOTS_FAILED, failureMessageKey: 'errors.updateSlotsFailed' });
                });
        },
};

const undefinedState: IBookingState = {
    loading: false,
    inFlight: {},
    failed: false,
    status: BookingStatus.Unknown,
    booking: undefined,
    charge: undefined,
    details: undefined,
    appliedCodes: [],
    codeApplied: undefined,
    readyForPayment: false,
    availableAddOns: [],
    availableDigitalAddOns: [],
    selectedAddOns: [],
    selectedDigitalAddOns: [],
    bookingAddOns: [],
    bookingDigitalAddOns: [],
};

// Per-flow in-flight tracking. `loading` is derived: true while any flow is active,
// false only when all flows have settled. Prevents one flow's response from clearing
// the loading flag while another concurrent request is still in flight.
const setInFlight = (state: IBookingState, key: BookingFlowKey, value: boolean): IBookingState => {
    const next: BookingInFlight = { ...state.inFlight };
    if (value) {
        next[key] = true;
    } else {
        delete next[key];
    }
    return { ...state, inFlight: next, loading: Object.keys(next).length > 0 };
};

export const reducer: Reducer<IBookingState> = (
    state: IBookingState | undefined,
    incomingAction: Action
): IBookingState => {
    const action: KnownBookingActions = incomingAction as KnownBookingActions;
    if (state === undefined) return undefinedState;

    switch (action.type) {
        case BOOKING_DETAILS_LOADING: {
            return {
                ...setInFlight(state, 'detailsLoad', true),
                details: undefined,
                failed: false,
            };
        }
        case BOOKING_DETAILS_LOADED: {
            return {
                ...setInFlight(state, 'detailsLoad', false),
                details: action.details,
                failed: !action.success,
            };
        }
        case CHECK_BOOKING_LOADED: {
            var bookingStatus = action.details?.bookingStatusTypeId === 5 ? BookingStatus.Expired : state.status;
            bookingStatus = action.details?.bookingStatusTypeId === 2 ? BookingStatus.Active : bookingStatus;
            bookingStatus = action.details?.bookingStatusTypeId === 8 ? BookingStatus.Reserved : bookingStatus;

            return {
                ...setInFlight(state, 'detailsLoad', false),
                details: action.details,
                failed: !action.success,
                status: bookingStatus,
            };
        }
        case EXPIRE_BOOKING_REQUESTED: {
            return {
                ...setInFlight(state, 'expire', true),
                failed: false,
            };
        }
        case EXPIRE_BOOKING_SUCCESS: {
            return {
                ...setInFlight(state, 'expire', false),
                failed: false,
                failedMessageKey: undefined,
                booking: undefined,
                charge: undefined,
                status: BookingStatus.Unknown,
                appliedCodes: [],
                bookingAddOns: [],
                bookingDigitalAddOns: []
            };
        }
        case EXPIRE_BOOKING_COMPLETE: {
            return {
                ...setInFlight(state, 'expire', false),
                booking: undefined,
                charge: undefined,
                status: BookingStatus.Unknown,
                appliedCodes: [],
            };
        }
        case APPLY_CODE_FAILED: {
            return {
                ...setInFlight(state, 'discountCode', false),
                failed: true,
                codeApplied: false,
                codeResponse: action.codeResponse,
            };
        }
        case DISCOUNT_CODE_APPLIED: {
            return {
                ...setInFlight(state, 'discountCode', false),
                failed: false,
                booking: action.booking,
                charge: action.charge,
                appliedCodes: action.codes,
                codeApplied: true,
                codeResponse: DiscountCodeResponseType.Available,
            };
        }
        case DISCOUNT_CODE_REMOVED: {
            return {
                ...setInFlight(state, 'discountCode', false),
                failed: false,
                booking: action.booking,
                charge: action.charge,
                appliedCodes: action.codes,
                codeApplied: undefined,
                codeResponse: undefined,
            };
        }
        case DISCOUNT_CODE_LOADING: {
            return {
                ...setInFlight(state, 'discountCode', true),
                failed: false,
            };
        }
        case RESERVE_BOOKING_REQUESTED: {
            return {
                ...setInFlight(state, 'reserve', true),
                failed: false,
                booking: undefined,
                charge: undefined,
                appliedCodes: [],
            };
        }
        case RESERVE_BOOKING_SUCCESS: {
            return {
                ...setInFlight(state, 'reserve', false),
                failed: false,
                failedMessageKey: undefined,
                booking: action.booking,
                charge: action.charge,
                status: BookingStatus.Pending,
                codeApplied: action.appliedCodes.length > 0 ? true : state.codeApplied,
                appliedCodes: action.appliedCodes,
                availableDigitalAddOns: action.availableDigitalAddOns,
                availableAddOns: action.availableAddOns,
                selectedDigitalAddOns: action.selectedDigitalAddOns,
                selectedAddOns: action.selectedAddOns,
            };
        }
        case RESERVE_BOOKING_FAILED: {
            return {
                ...setInFlight(state, 'reserve', false),
                failed: true,
                failedMessageKey: action.failureMessageKey,
                booking: undefined,
                charge: undefined,
                appliedCodes: [],
            };
        }
        case CHANGE_USER: {
            return {
                ...state,
                booking: action.booking,
            };
        }
        case UPDATE_BOOKING_REQUESTED: {
            return {
                ...setInFlight(state, 'bookingCompoundUpdate', true),
                readyForPayment: false,
            };
        }
        case UPDATE_BOOKING_SUCCESS: {
            return {
                ...state,
                failed: false,
            };
        }
        case UPDATE_BOOKING_FAILED: {
            return {
                ...setInFlight(state, 'bookingCompoundUpdate', false),
                failed: true,
                failedMessageKey: action.failureMessageKey,
            };
        }
        case UPDATE_BOOKING_COMPLETE: {
            return {
                ...setInFlight(state, 'bookingCompoundUpdate', false),
                readyForPayment: true,
            };
        }
        case CONFIRM_BOOKING_REQUESTED: {
            return {
                ...setInFlight(state, 'confirm', true),
                failed: false
            };
        }
        case CONFIRM_BOOKING_FAILED: {
            return {
                ...setInFlight(state, 'confirm', false),
                failed: true
            };
        }
        case CONFIRM_BOOKING_SUCCESS: {
            return {
                ...setInFlight(state, 'confirm', false),
                failed: false,
            };
        }
        case PRESELECT_CODE: {
            return {
                ...state,
                preSelectedCode: action.code,
            };
        }
        case CLEAR_CODE: {
            return {
                ...state,
                codeApplied: undefined,
                preSelectedCode: undefined,
            };
        }
        case CLEAR_BOOKING: {
            return {
                ...undefinedState,
                preSelectedCode: state.preSelectedCode,
                priceSets: state.priceSets,
            };
        }
        case UPDATE_PRICE_SETS: {
            return {
                ...state,
                priceSets: action.priceSets,
            };
        }
        case SET_SELECTED_ADDONS: {
            return {
                ...state,
                selectedAddOns: action.selectedAddOns,
                selectedDigitalAddOns: action.selectedDigitalAddOns,
            };
        }
        case UPDATE_ADDONS_REQUESTED: {
            return setInFlight(state, 'addons', true);
        }
        case UPDATE_ADDONS_COMPLETE: {
            // Add-on flows also dispatch UPDATE_BOOKING_REQUESTED (which sets bookingCompoundUpdate),
            // but complete through here instead of UPDATE_BOOKING_COMPLETE — clear both flags.
            const withAddonsCleared = setInFlight(state, 'addons', false);
            return {
                ...setInFlight(withAddonsCleared, 'bookingCompoundUpdate', false),
                readyForPayment: true,
                bookingAddOns: action.bookingAddOns,
                bookingDigitalAddOns: action.bookingDigitalAddOns,
                charge: action.charge,
            };
        }
        case CHARGE_REFRESHED: {
            return {
                ...state,
                charge: action.charge,
            };
        }
        case UPDATE_PAYMENT_OPTION_REQUESTED: {
            return {
                ...setInFlight(state, 'paymentOption', true),
                readyForPayment: false,
            };
        }
        case UPDATE_PAYMENT_OPTION_COMPLETE: {
            return {
                ...setInFlight(state, 'paymentOption', false),
                readyForPayment: true,
                charge: action.charge,
            };
        }
        case UPDATE_PAYMENT_OPTION_FAILED: {
            return {
                ...setInFlight(state, 'paymentOption', false),
                readyForPayment: true,
                failed: true,
            };
        }
        case UPDATE_SLOTS_REQUESTED: {
            return {
                ...setInFlight(state, 'bookingSlots', true),
                readyForPayment: false,
                failed: false,
                failedMessageKey: undefined,
            };
        }
        case UPDATE_SLOTS_SUCCESS: {
            return {
                ...setInFlight(state, 'bookingSlots', false),
                booking: action.booking,
                charge: action.charge,
                readyForPayment: true,
                appliedCodes: action.appliedCodes,
                availableDigitalAddOns: action.availableDigitalAddOns,
                availableAddOns: action.availableAddOns,
            };
        }
        case UPDATE_SLOTS_FAILED: {
            return {
                ...setInFlight(state, 'bookingSlots', false),
                readyForPayment: true,
                failed: true,
                failedMessageKey: action.failureMessageKey,
            };
        }
        default:
            return state;
    }
};

